/**
 * @license
 * Copyright TWISS.IO
 * All Rights Reserved.
 * Licensed under the MIT License.
 */

import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  ViewChild,
  ViewChildren, QueryList,
  ChangeDetectorRef
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MAT_BOTTOM_SHEET_DATA, MatStepper, MatAutocompleteTrigger } from "@angular/material";
import {
  FormBuilder,
  Validators,
  AbstractControl,
  FormGroup,
  FormArray
} from "@angular/forms";
import { Observable, Subscription, Subject } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { MatBottomSheetRef } from "@angular/material/bottom-sheet";
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS
} from "@angular/material-moment-adapter";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE
} from "@angular/material/core";

import { Submission } from "@app/models";
import { ApiService, AuthService } from "@app/@core/services";
import { PageService } from "@auth/services";
import { AssociatesClientRelationshipsService } from '@app/@shared/services/associates/client-relationships/client-relationships.service';
import { MessageService } from '@app/@shared/components/message/message.service';

// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from "moment";
// tslint:disable-next-line:no-duplicate-imports
import { default as _rollupMoment } from "moment";

const moment = _rollupMoment || _moment;

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const update_business_date_formats = {
  parse: {
    dateInput: "LL"
  },
  display: {
    dateInput: "LL",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY"
  }
};

import * as Fuse from "fuse.js";

@Component({
  selector: "associates-business-update-dialog",
  templateUrl: "./business-update-dialog.component.html",
  styleUrls: ["./business-update-dialog.component.scss"],
  providers: [
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: update_business_date_formats }
  ]
})
export class AssociatesBusinessUpdateDialogComponent implements OnInit, OnDestroy {
  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private formBuilder: FormBuilder,
    public mbsRef: MatBottomSheetRef,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    public authService: AuthService,
    private pageService: PageService,
    private _notification: MessageService,
    private apiService: ApiService,
    private assClientService: AssociatesClientRelationshipsService
  ) { }

  private subs_getSelections: Subscription;

  is_update: boolean = false;
  processing = new Subject<boolean>();
  dataLoaded = new Subject<boolean>();
  authForm_default_values = {};

  countries;

  public authForm: FormGroup = this.formBuilder.group({
    // Business Record
    business_name: [null],
    business_uen: [null],
    // Business :: Contact
    email: [null],
    home_no: [null],
    mobile_no: [null],
    fax_no: [null],
    // Business :: Address
    block: [null],
    street: [null],
    unit: [null],
    building: [null],
    city: [null],
    postal: [null],
    country_slug: [null]
  });
  get business_name() {
    return this.authForm.controls.business_name;
  }
  get business_uen() {
    return this.authForm.controls.business_uen;
  }
  get email() {
    return this.authForm.controls.email;
  }
  get home_no() {
    return this.authForm.controls.home_no;
  }
  get mobile_no() {
    return this.authForm.controls.mobile_no;
  }
  get fax_no() {
    return this.authForm.controls.fax_no;
  }
  get block() {
    return this.authForm.controls.block;
  }
  get street() {
    return this.authForm.controls.street;
  }
  get unit() {
    return this.authForm.controls.unit;
  }
  get building() {
    return this.authForm.controls.building;
  }
  get city() {
    return this.authForm.controls.city;
  }
  get postal() {
    return this.authForm.controls.postal;
  }
  get country_slug() {
    return this.authForm.controls.country_slug;
  }

  ngOnInit() {
    this.getSelections();
  }

  ngOnDestroy() {
    if (this.subs_getSelections) this.subs_getSelections.unsubscribe();
  }

  getSelections() {
    this.subs_getSelections = this.apiService
      .post("selections", {
        lists: [
          "country",
        ]
      })
      .subscribe(res => {
        let data = res["data"];
        this.countries = data["country"];
        this.updateModel(this.data);
      });
  }

  getUpdatedValues() {
    const updatedFormValues = {};
    this.authForm['_forEachChild']((control, name) => {
      if (control.value != this.authForm_default_values[name]) {
          updatedFormValues[name] = control.value;
      }
    });
    return updatedFormValues;
  }

  submitForm() {
    this.processing.next(true);
    this.assClientService.updateClient(this.data.uuid, this.getUpdatedValues())
      .subscribe(res => {
        if (res.error === false) {
          // Case Created
          this.mbsRef.dismiss();
          this.pageService.triggerParentRefresh(true);
          this._notification.create('success', 'Success! Business data updated successfully.', { Position: 'top', Style: 'bar', Duration: 2000 });
        }
        this.processing.next(false);
      });
  }

  updateModel(data = null) {
    if (data) {
      // Reset Insurance Details
      this.authForm.reset();
      this.authForm.patchValue({
        business_name: data.business_name,
        business_uen: data.business_uen,
        // Business :: Contact
        email: data.contact_information.email,
        home_no: data.contact_information.home_no,
        mobile_no: data.contact_information.mobile_no,
        fax_no: data.contact_information.fax_no,
        // Business :: Address
        block: data.address_information.block,
        street: data.address_information.street,
        unit: data.address_information.unit,
        building: data.address_information.building,
        city: data.address_information.city,
        postal: data.address_information.postal,
        country_slug: data.address_information.country_slug
      });

      this.authForm_default_values = this.authForm.value;
    } else {
      this.authForm.patchValue({
        business_name: null,
        business_uen: null,
        // Individual :: Contact
        email: null,
        home_no: null,
        mobile_no: null,
        fax_no: null,
        // Individual :: Address
        block: null,
        street: null,
        unit: null,
        building: null,
        city: null,
        postal: null,
        country_slug: null
      });
    }
    this.dataLoaded.next(true);
  }

  resetForm(ref = null, data = null) {
    if (!ref) {
      this.authForm.reset();
    } else {
      const form_field = this.authForm.get(ref);
      if (!form_field.validator) return form_field.reset();
      const validator = form_field.validator({} as AbstractControl);
      if (validator && validator.required) form_field.setValue("");
      else form_field.reset();
    }
  }

  private _filter(value, model): string[] {
    const filterValue = value ? value.toLowerCase() : "";
    return model.filter(option =>
      option.slug.toLowerCase().includes(filterValue)
    );
  }
}
